import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
} from '@angular/core';
import { type Chart, type LegendItem } from 'chart.js';

import { type ChartType } from '../enums';

import { ItemComponent } from './item/item.component';

@Component({
  selector: 'myre-chart-legend',
  templateUrl: './chart-legend.component.html',
  styleUrls: ['./chart-legend.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ItemComponent],
})
export class ChartLegendComponent {
  private readonly _changeDetectionRef = inject(ChangeDetectorRef);

  /**
   * The chart associated with the legend.
   * Allows retrieving data to display via the generateLabels function.
   * Enables interaction with the chart to show/hide or highlight data.
   */
  @Input()
  public set chart(chart: Chart<ChartType>) {
    if (!chart) {
      return;
    }

    this._chart = chart;
  }

  @Input()
  public items: LegendItem[] = [];

  /**
   * When clicking on a legend item, toggle the visibility of the corresponding data in the chart.
   */
  @Input()
  public canToggleDataVisibility = true;

  @Input()
  public modeLinkingHover: 'none' | 'singleDataset' | 'multiDataSetMultiData' =
    'singleDataset';

  private _chart: Chart<ChartType>;

  /**
   * Changing the visibility of a data in the chart
   */
  protected _toggleDataVisibility(index: number): void {
    if (!this._chart) {
      return;
    }

    this._chart.toggleDataVisibility(index);
    this._chart.update();

    this.items[index].hidden = !this.items[index].hidden;
    this._changeDetectionRef.detectChanges();
  }

  /**
   * Simulate a mouse event on the chart to highlight the data
   */
  protected _displayHoverStateInGraph(index: number, isHover: boolean): void {
    if (this.modeLinkingHover === 'none' || !this._chart) {
      return;
    }

    const rectangle = this._chart.canvas.getBoundingClientRect();

    let obj: any = null;

    switch (this.modeLinkingHover) {
      case 'singleDataset': {
        obj = this._chart.getDatasetMeta(0).data[index];

        break;
      }
      case 'multiDataSetMultiData': {
        [obj] = this._chart.getDatasetMeta(index).data;

        break;
      }
      default: {
        throw Error('modeLinkingHover not found');
      }
    }

    const point = obj.getCenterPoint();

    let mouseMoveEvent: MouseEvent;

    if (isHover) {
      mouseMoveEvent = new MouseEvent('mousemove', {
        clientX: rectangle.left + point.x,
        clientY: rectangle.top + point.y,
      });
    } else {
      mouseMoveEvent = new MouseEvent('mouseout', {});
    }

    this._chart.canvas.dispatchEvent(mouseMoveEvent);
  }
}
